/**
 * Department filter chips
 *
 * Dynamically builds filter chips from available departments in the API data.
 *
 * @package Interact_26
 */

import { type CareersConfig } from './types';

export type FilterChangeCallback = (activeDepartments: string[]) => void;

/**
 * Initialize department filter chips.
 *
 * Renders pill-shaped toggle buttons for the given departments and calls the
 * onChange callback when filters change.
 */
export const initFilters = (
  container: HTMLElement,
  config: CareersConfig,
  departments: string[],
  onChange: FilterChangeCallback,
): void => {
  if (departments.length === 0) {
    container.innerHTML = '';
    return;
  }

  const activeDepartments = new Set<string>();

  const render = () => {
    container.innerHTML = '';

    // Label (own row above chips)
    const label = document.createElement('span');
    label.className = 'careers-filters__label';
    label.textContent = config.i18n.filterByDepartment;
    container.appendChild(label);

    // Chips wrapper
    const chipsRow = document.createElement('div');
    chipsRow.className = 'careers-filters__chips';

    // "All departments" chip
    const allChip = createChip(config.i18n.allDepartments, activeDepartments.size === 0, () => {
      activeDepartments.clear();
      render();
      onChange([]);
    });
    chipsRow.appendChild(allChip);

    // Department chips
    for (const dept of departments) {
      const isActive = activeDepartments.has(dept);
      const chip = createChip(dept, isActive, () => {
        if (isActive) {
          activeDepartments.delete(dept);
        } else {
          activeDepartments.add(dept);
        }
        render();
        onChange([...activeDepartments]);
      });
      chipsRow.appendChild(chip);
    }

    // Clear button (visible when any filter is active)
    if (activeDepartments.size > 0) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'careers-filters__clear';
      clearBtn.textContent = config.i18n.clearFilters;
      clearBtn.addEventListener('click', () => {
        activeDepartments.clear();
        render();
        onChange([]);
      });
      chipsRow.appendChild(clearBtn);
    }

    container.appendChild(chipsRow);
  };

  render();
};

/**
 * Create a single filter chip button.
 */
const createChip = (label: string, isActive: boolean, onClick: () => void): HTMLButtonElement => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = isActive
    ? 'careers-filters__chip careers-filters__chip--active'
    : 'careers-filters__chip';
  button.textContent = label;
  button.setAttribute('aria-pressed', String(isActive));
  button.addEventListener('click', onClick);
  return button;
};
